import type { RolePillTone } from "../ui";

// The role vocabulary of the user directory: the codes the admin user list can show, the order it
// ranks them in, the hue each gets, and the two questions a Rooli filter has to answer — which
// value a row belongs under, and whether a row can belong under none of them.
//
// The source of truth for WHICH codes exist is the backend, not this file:
// `UserAdminService.BusinessRoles` filters the Keycloak client roles down to the CLMS business roles
// before they ever reach the DTO. This list must cover that set exactly, and
// `src/i18n/user-roles-i18n.test.mjs` reads both C# files and fails the build when it does not —
// because the failure mode is silent. A projected code missing from here has no label, no hue, and
// (before this file existed) no filter value: its holders simply did not appear under any role and
// nothing said so. `JOHDON_RAPORTTI` was in exactly that state, the three `MJ_` codes were in it from
// MJ-0.4 until ADR-0016 §4.6, and `HRM_HENKILOSTOASIANTUNTIJA` from the authority's own task (ADR-0020
// §16 task 1) until this entry.
//
// Those three are a visibility decision and NOT an authority one, which is the thing to know before
// changing them: ADR-0016 §4.5 keeps `ADMIN` out of every muutosjohtaminen route and this file reaches
// no route at all. It names who holds what in the tenant's own directory — the SPA's capability map
// (auth/capabilities.ts) is the separate question of which surfaces a code opens, and it still carries
// only `MJ_VASTUUKONSULTTI`.

/**
 * Every business role the backend can project, ordered by authority — the first code a user holds is
 * the one the row's pill shows.
 *
 * The ordering rule is "who can change the most", not "who can see the most":
 * PLATFORM_ADMIN → ADMIN → HR → CONTENT_MANAGER are the write authorities in descending breadth,
 * then the read-only and scope-limited ones.
 *
 * `JOHDON_RAPORTTI` (johdon raporttikäyttäjä, US-E9.1) sits below CONTENT_MANAGER and above ESIMIES.
 * Below, because it holds no configuration authority at all — `Roles.cs` keeps it out of
 * QuestionnaireManagement, TenantParameterManagement, ViewScopeGrantManagement and UnitCatalogRead,
 * deliberately (FR-Q.50): granting is done TO a leadership viewer, never BY one. Above ESIMIES,
 * because when somebody holds both, "johdon raporttikäyttäjä" is the more informative thing to say
 * about them in a directory — it is a named leadership position whose reach is the tenant's report
 * data, where the manager role is a line-management fact about one team.
 *
 * The three muutosjohtaminen codes (ADR-0016 §4.1, §4.6) are placed by the SAME rule, which is a
 * statement about the module's reach and not about its importance:
 *
 *   • `MJ_VASTUUKONSULTTI` below CONTENT_MANAGER — a write authority, but over ONE module's case file
 *     rather than anything tenant-wide, and in no authority set outside it (ADR-0016 §4.2).
 *   • `MJ_JOHDON_RAPORTTI` immediately below `JOHDON_RAPORTTI`. Adjacent because they are the same
 *     shape of thing — a named leadership reader — and below because its reach is one programme's
 *     PUBLISHED report where the other's is the tenant's granted report scopes. The adjacency is the
 *     point: §4.3 made these two different codes, and a directory that showed them apart would be the
 *     place that collision stops being obvious.
 *   • `MJ_TOIMENPITEEN_OMISTAJA` directly above OPPIJA — it writes, but only the state of its own
 *     actions (§4.5 property 3), the same self-scoping that puts OPPIJA last. OPPIJA stays last
 *     because nearly everyone holds it, so it is the least informative pill a row can show.
 *
 * `HRM_HENKILOSTOASIANTUNTIJA` (ADR-0020 §5) sits immediately below CONTENT_MANAGER and above
 * MJ_VASTUUKONSULTTI, by the same rule. Below CONTENT_MANAGER because it holds no configuration
 * authority at all — `Roles.cs` carries it in `HrmPersonnelRecord` and `EmploymentRecordView` and in
 * nothing else — which is `JOHDON_RAPORTTI`'s reason one row further down. Above MJ_VASTUUKONSULTTI
 * because what it writes is every person in the tenant's employment record, henkilötunnus and salary
 * included, where the consultant writes one programme's case file.
 *
 * It is the one entry whose ABSENCE the ADR argued from: §5 point 6 answers "a tenant `ADMIN` may grant
 * an authority it may not hold" with the grant being visible — *"the admin user list resolves roles live
 * from it"* — and that was a statement about this list. Note that the Rooli FILTER, not the pill, is what
 * carries it: a holder who is also a CONTENT_MANAGER shows the content pill, and `matchesRoleFilter`
 * still files them under HRM_HENKILOSTOASIANTUNTIJA, because it matches any role held.
 */
export const ROLE_VOCABULARY = [
  "PLATFORM_ADMIN",
  "ADMIN",
  "HR",
  "CONTENT_MANAGER",
  "HRM_HENKILOSTOASIANTUNTIJA",
  "MJ_VASTUUKONSULTTI",
  "JOHDON_RAPORTTI",
  "MJ_JOHDON_RAPORTTI",
  "ESIMIES",
  "COMPLIANCE_AUDITOR",
  "MJ_TOIMENPITEEN_OMISTAJA",
  "OPPIJA",
] as const;

export type RoleCode = (typeof ROLE_VOCABULARY)[number];

/**
 * Verso hue per role. There are five tones and twelve roles, so hues repeat on purpose: colour only
 * reinforces the always-present label, it never carries meaning alone. The tone says what kind of
 * authority a role is, which is why the neutral group is the roles holding none OVER OTHER PEOPLE —
 * JOHDON_RAPORTTI, MJ_JOHDON_RAPORTTI, COMPLIANCE_AUDITOR, MJ_TOIMENPITEEN_OMISTAJA and OPPIJA. Not
 * "read-only": OPPIJA has always written its own attempts and the action owner writes its own
 * actions' state, and both are still facts about nobody but themselves. That repetition is the
 * honest grouping, not a gap waiting for a sixth colour.
 *
 * MJ_VASTUUKONSULTTI takes CONTENT_MANAGER's hunaja because it is the same KIND of authority over a
 * narrower body: a configuration authority over one body of content, not over the tenant.
 *
 * HRM_HENKILOSTOASIANTUNTIJA takes HR's salvia by that same rule, and it is the one tone choice in
 * this file that had to be argued rather than read off: the two codes are the tenant's PEOPLE-data
 * authority, split in two only because HR's sitaattinäkymä has to stay external (`Roles.cs`,
 * HrmPersonnelExpert — *"lukija on ulkopuolinen HR-asiantuntija, ei työnantaja"*). Not neutral,
 * because this is the furthest-reaching authority in the product over other people's data; not
 * hunaja, which says "configuration over a body of content"; not coral, which is line management.
 * The hue sharing HR's is the honest statement, and the label is what distinguishes them — the same
 * trade the MJ_ codes already make.
 */
export const ROLE_TONE: Record<string, RolePillTone> = {
  PLATFORM_ADMIN: "info", // indigo
  ADMIN: "info",
  HR: "good", // salvia
  CONTENT_MANAGER: "warn", // hunaja
  HRM_HENKILOSTOASIANTUNTIJA: "good",
  MJ_VASTUUKONSULTTI: "warn",
  JOHDON_RAPORTTI: "neutral",
  MJ_JOHDON_RAPORTTI: "neutral",
  ESIMIES: "accent", // coral
  COMPLIANCE_AUDITOR: "neutral",
  MJ_TOIMENPITEEN_OMISTAJA: "neutral",
  OPPIJA: "neutral",
};

/**
 * The filter value for everyone the vocabulary does not name: a user with no business role at all,
 * and — should the backend ever project a code this file has not caught up with — that user too.
 * It is what makes the filter TOTAL. Labelled `users.role.none` / "Ei roolia", the same words the
 * pill already uses for a roleless row.
 */
export const ROLE_FILTER_NONE = "none";

/**
 * The "no filtering" value, so the control has a resting state that is not a role.
 *
 * It is the EMPTY string because that is the filter scope's one resting state (`NO_FILTER` in
 * `lib/filterScope.ts`): a control set to it is not counted on the badge, not ringed, and not
 * consulted at all. A second spelling of "everything" — "all" — would be an active value that
 * happens to match every row, and the badge would say a filter was on while the table was whole.
 */
export const ROLE_FILTER_ALL = "";

/** Every value a Rooli filter offers, in the order it offers them. */
export const ROLE_FILTER_VALUES = [ROLE_FILTER_ALL, ...ROLE_VOCABULARY, ROLE_FILTER_NONE] as const;

/** The single role pill to render for a row: the highest-authority code the user holds. */
export function primaryRole(roles: readonly string[]): string | null {
  for (const code of ROLE_VOCABULARY) {
    if (roles.includes(code)) return code;
  }
  return roles[0] ?? null;
}

/**
 * Does this user belong under `value` in the Rooli filter?
 *
 * ANY role the user holds, NOT the single primary one the row's pill displays. The decision, and
 * why it goes this way:
 *
 * The pill has to pick one — a row has one cell — but the filter does not, and matching only the
 * primary role would hide people from the question actually being asked. An admin who filters to
 * ESIMIES is asking "who are the managers", and a person who is both HR and ESIMIES is one of them;
 * under primary-only matching she is missing from the answer, listed under HR, with nothing to
 * suggest the list is short. That is the same silent hole this file exists to close, one level up.
 *
 * The cost is visible and tolerable: a multi-role user appears under more than one filter value,
 * and her row still shows the single highest-authority pill. Counts across values therefore sum to
 * more than the directory — which is correct, because the roles do too.
 *
 * Totality: every user matches at least one value. A user holding any vocabulary code matches that
 * code; a user holding none — no roles, or only codes this file does not know — matches
 * ROLE_FILTER_NONE. There is no third case, so no user can be invisible to every value.
 */
export function matchesRoleFilter(roles: readonly string[], value: string): boolean {
  if (value === ROLE_FILTER_ALL) return true;
  if (value === ROLE_FILTER_NONE) return !roles.some((code) => hasVocabularyEntry(code));
  return roles.includes(value);
}

/** Is this code one the vocabulary names (and therefore labels, tones and offers as a filter value)? */
export function hasVocabularyEntry(code: string): code is RoleCode {
  return (ROLE_VOCABULARY as readonly string[]).includes(code);
}
